<template>
  <div class="container">
    <mds-section bold :size="3" border="none" title="Morningstar Portable Document Format (PDF) Portal">
      This tool is for internal use (testing) or demo purpose only. The Portal
      link should not be shared with outside clients. If you have question,
      please contact the PDF team on the
      <a class="link" target="_blank"
        href="https://teams.microsoft.com/l/channel/19%3A10dc2d12e700495a97b64ece22d8086a%40thread.skype/General?groupId=46677973-be25-44ab-85ee-90646fed148e&tenantId=d2d302fb-0aef-4773-94a5-7950c6f64a35">
        Teams Channel.
      </a>
    </mds-section>
    <div class="search-container">
      <mds-search-field v-model="searchValue" placeholder="Enter investment name, ticker, or secid"
        constiation="primary" label="Search" size="touch"></mds-search-field>
      <mds-search-results v-show="searchValue.length" class="search-results">
        <mds-loader v-if="isLoading" aria-label="Loading"></mds-loader>
        <mds-table v-else-if="searchResults.length" row-hover zebra-stripes>
          <mds-thead>
            <mds-th :style="{ width: '55%' }">Name</mds-th>
            <mds-th :style="{ width: '15%' }">Symbol</mds-th>
            <mds-th :style="{ width: '15%' }">Country</mds-th>
            <mds-th :style="{ width: '15%' }">Type</mds-th>
          </mds-thead>
          <mds-tbody>
            <mds-tr v-for="result in searchResults" :key="result.secId" @click="redirectToReport(result)">
              <mds-td>{{ result.name }}</mds-td>
              <mds-td>{{ result.exchange }}</mds-td>
              <mds-td>{{ result.country }}</mds-td>
              <mds-td>{{ result.type }}</mds-td>
            </mds-tr>
          </mds-tbody>
        </mds-table>
      </mds-search-results>
    </div>
    <div class="action-buttons">
      <mds-radio-button checked name="reportType" value="equityAnalyst" @click="handleReportType('equity-analyst')">
        Equity Analyst Report
      </mds-radio-button>
      <mds-radio-button name="reportType" value="ESG" @click="handleReportType('esg')">
        ESG Report
      </mds-radio-button>
      <mds-radio-button name="reportType" value="equityNote" @click="handleReportType('equity-note')">
        Equity Note Report
      </mds-radio-button>
      <mds-radio-button name="reportType" value="equityCompany" @click="handleReportType('equity-company')">
        Equity Company Report
      </mds-radio-button>
    </div>
  </div>
</template>

<script>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import debounce from "lodash/debounce";
import MdsInput from "@mds/input-vue3";
import MdsSearchField from "@mds/search-field-vue3";
import MdsRadioButton from "@mds/radio-button-vue3";
import { MdsSearchResults } from "@mds/search-results-vue3";
import MdsSection from "@mds/section-vue3";
import MdsLoader from "@mds/loader-vue3";
import {
  MdsTable,
  MdsThead,
  MdsTbody,
  MdsTr,
  MdsTh,
  MdsTd,
} from "@mds/data-table-vue3";

export default {
  name: "sal-search",
  components: {
    MdsSearchField,
    MdsRadioButton,
    MdsSearchResults,
    MdsSection,
    MdsLoader,
    MdsInput,
    MdsTable,
    MdsThead,
    MdsTbody,
    MdsTh,
    MdsTr,
    MdsTd,
  },
  setup() {
    const isLoading = ref(false);
    const searchValue = ref("");
    const reportType = ref("equity-analyst");
    const authType = ref("maas");
    const fundServCode = ref("");
    const searchResults = ref([]);
    const router = useRouter();

    const handleReportType = (type) => {
      reportType.value = type;
    };

    const parseStockData = (data) => {
      if (data && data.m && data.m.length > 0) {
        const rawData = data.m[0].r;
        if (rawData) {
          searchResults.value = rawData.map((v) => {
            return {
              name: v.OS01W,
              ticker: v.OS001,
              secId: v.SecId,
              pId: v.OS06Y || v.SecId,
              country: v.XI018,
              type: v.OS010,
              subType: v.ST816,
              exchange: v.LS01Z.substring(6),
            };
          });
        }
      }
    };

    const getEndpoint = () => {
      const { host } = window.location;
      if (host.indexOf("sal-qa.morningstar.com") > -1) {
        return `https://salservice-qa.morningstar.com`;
      }
      if (host.indexOf("sal-stg.morningstar.com") > -1) {
        return `https://salservice-stg.morningstar.com`;
      }
      if (host.indexOf("sal-uat.morningstar.com") > -1) {
        return `https://salservice-uat.morningstar.com`;
      }
      if (host.indexOf("sal.morningstar.com") > -1) {
        return `https://salservice.morningstar.com`;
      }
      return `https://salservice-stg.morningstar.com`;
    };

    const searchFund = debounce((val) => {
      isLoading.value = true;
      const url = `${getEndpoint()}/sal-service/stock/idservice/suggestion?f=SecId,OS06Y,OS01W,OS001,OS010,XI018,OS01X,LS01Z,ST816&p=OS001,OS01W,SecId&t=0&n=10&o=1&v=2&pd=SAL&s=0&k=${val}`;
      fetch(url)
        .then((response) => response.json())
        .then(parseStockData)
        .finally(() => (isLoading.value = false))
    }, 500);

    const redirectToReport = (result) => {
      const query = {
        shareClassId: result.secId,
        securityType: result.type,
        fundServCode: fundServCode.value,
        authType: authType.value,
      };

      if ( reportType.value === "esg") {
        router.push({
          path: `${reportType.value}`,
          query,
        });
      }
      if (result.type === "ST" && reportType.value === "equity-company") {
        router.push({
          path: `${reportType.value}`,
          query,
        });
      }
      if (result.type === "ST" && reportType.value === "equity-note") {
        router.push({
          path: `${reportType.value}`,
          query,
        });
      }
      if (result.type === "ST" && reportType.value === "equity-analyst") {
        router.push({
          path: `${reportType.value}`,
          query,
        });
      }
    };

    watch(searchValue, (val) => {
      if (val.length) {
        searchFund(val);
      }
    })

    return {
      isLoading,
      searchValue,
      reportType,
      redirectToReport,
      fundServCode,
      handleReportType,
      searchResults,
    }
  },
};
</script>

<style lang="scss">
@import "@mds/link-vue3";

.container {
  min-height: 500px;
  max-width: 730px;
  margin-left: auto;
  margin-right: auto;

  .mds-section {
    margin-top: 30px;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 20px;

    .mds-section__content {
      padding-bottom: 20px;
    }
  }

  .link {
    @include mds-link();
  }

  .action-buttons {
    margin-top: 20px;

    label {
      margin: 14px;
    }

    .mds-radio-button__text {
      font-weight: bold;
    }
  }

  .gmb {
    margin-top: 25px;
    margin-left: 30px;
  }

  .plan-center {
    margin-top: 30px;
    margin-left: 30px;
  }

  .gmb,
  .plan-center {
    a {}
  }

  .search-container {
    .search-results {
      max-width: 720px;
      margin-top: 5px;

      tr {
        cursor: pointer;
      }
    }
  }

  .fundserv {
    float: right;
    width: 200px;
    margin-top: 10px;
  }

  .auth-type {
    display: flex;
    margin-left: 10px;
    margin-top: 50px;

    .mds-radio-button:nth-child(n + 2) {
      margin-left: 10px;
    }
  }

  @media only screen and (max-width: 500px) {
    .fundserv {
      float: none;
    }
  }
}
</style>
